import { Avatar, Button, Divider, Drawer, Flex, Grid, Group, Input, Text, Tooltip } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { IconInfoSquare, IconPhoto, IconTextPlus } from "@tabler/icons-react";
import React from "react";
import { useSelector } from "react-redux";
import { merchantApi } from "../../apis";
import { IGeneral } from "../../interfaces/IGeneral";
import { IMerchant } from "../../interfaces/IMerchant";
import { IReference } from "../../models/General";
import { dateFormat } from "../../utils/date";
import { renderName } from "../../utils/render-name";
import { PartnerManagementBankAccountForm } from "../bank-account/form";
import { ColumnType, ITableRef, Table } from "../table";
import { Tag } from "../tags";
import RenderText from "../ui/render-text";
import { MerchantForm } from "./form";

export function PartnerManagementMerchantList({ action, onSuccess }: { action: string[]; onSuccess: () => void }) {
  const ref = React.useRef<ITableRef>(null);
  const [account, setAccount] = React.useState<string[]>([]);
  const { qpayMccCodes, zipCodes } = useSelector((state: { general: IGeneral }) => state.general);
  const [regNumber, setRegNumber] = React.useState("");
  const [debouncedRegNumber] = useDebouncedValue(regNumber, 600);

  const columns = useHeader({
    zipCodes,
    qpayMccCodes,
    onClick: (key, item) => {
      switch (key) {
        case "detail": {
          break;
        }
        case "account": {
          setAccount(["ADD", item as any]);
          break;
        }
      }
    },
  });

  const reload = () => {
    ref.current?.reload();
  };

  const onCancel = () => {
    setAccount([]);
  };

  return (
    <Flex direction={"column"} gap="md">
      <Grid mb="xs">
        <Grid.Col span={{ xs: 12, sm: 12, md: 6, lg: 3 }}>
          <Input
            value={regNumber}
            leftSectionWidth={80}
            placeholder="хайх"
            style={{ borderRight: "none", borderRadius: "4px 0 0 4px" }}
            leftSection={
              <Group w={"calc(100% - 20px)"} align="center">
                <Text size="sm" fw={450} c="#44566C">
                  {"ТТД"}
                </Text>
                <Text>:</Text>
              </Group>
            }
            onChange={(e) => {
              setRegNumber(e?.currentTarget?.value);
            }}
            minLength={7}
            maxLength={7}
          />
        </Grid.Col>
      </Grid>
      <Divider color="gray" mt={10} mb={10} />
      <Table
        ref={ref}
        name="partner-management-merchant.list"
        columns={columns}
        filters={{ regNumber: debouncedRegNumber, businessName: "" }}
        loadData={(data) => merchantApi.list(data!)}
      />
      <Drawer withCloseButton={false} title={false} position="right" padding={"lg"} size={"70%"} opened={action[0] === "ADD"} onClose={() => onSuccess()}>
        <MerchantForm action={action} onSuccess={onSuccess} reload={reload} />
      </Drawer>
      <Drawer withCloseButton={false} title={false} position="right" padding={"lg"} size={"70%"} opened={account[0] === "ADD"} onClose={() => setAccount([])}>
        <PartnerManagementBankAccountForm account={account} onSuccess={onSuccess} reload={reload} onCancel={onCancel} />
      </Drawer>
    </Flex>
  );
}

const useHeader = ({
  onClick,
  zipCodes,
  qpayMccCodes,
}: {
  onClick: (key: string, record: IMerchant) => void;
  zipCodes: IReference[];
  qpayMccCodes: IReference[];
}): ColumnType<IMerchant>[] => [
  {
    title: "#",
    width: "30px",
    render: (_r, index) => <RenderText text={index + 1} />,
  },
  {
    title: "Үйлдэл",
    sorter: true,
    dataIndex: "sku",
    render: (record) => {
      return (
        <Flex gap="sm">
          <Tooltip label="Дэлгэрэнгүй" position="bottom">
            <Button variant="light" radius={100} w={35} h={35} p={0} onClick={() => onClick("detail", record)}>
              <IconInfoSquare />
            </Button>
          </Tooltip>
          <Tooltip label="Данс нэмэх" position="bottom">
            <Button variant="light" radius={100} w={35} h={35} p={0} onClick={() => onClick("account", record)}>
              <IconTextPlus />
            </Button>
          </Tooltip>
        </Flex>
      );
    },
  },
  {
    title: "Зураг",
    dataIndex: "logo",
    render: (record) => {
      return (
        <Avatar src={`${record.logo}`} alt="">
          <IconPhoto />
        </Avatar>
      );
    },
  },
  {
    title: "Мерчант ID",
    sorter: true,
    dataIndex: "qpayMerchantId",
    render: (record) => <RenderText text={record?.qpayMerchantId || "-"} />,
  },
  {
    title: "Регистр",
    sorter: true,
    dataIndex: "regNumber",
    render: (record) => <RenderText text={record?.regNumber || "-"} />,
  },
  {
    title: "Нэр",
    sorter: true,
    dataIndex: "businessName",
    render: (record) => <RenderText text={record?.businessName || "-"} />,
  },
  {
    title: "MCC_Code",
    sorter: true,
    dataIndex: "qpayMerchantMccCode",
    render: (record) => <RenderText text={(qpayMccCodes || []).find((c: any) => c?.code === record?.qpayMerchantMccCode)?.name || "-"} />,
  },
  {
    title: "Эзний регистр",
    sorter: true,
    dataIndex: "qpayMerchantOwnerRegNum",
    render: (record) => <Tag color="blue" name={record?.qpayMerchantOwnerRegNum || "-"} />,
  },
  {
    title: "Эзний овог",
    sorter: true,
    dataIndex: "qpayMerchantOwnerLastName",
    render: (record) => <RenderText text={record?.qpayMerchantOwnerLastName || "-"} />,
  },
  {
    title: "Эзний нэр",
    sorter: true,
    dataIndex: "qpayMerchantOwnerFirstName",
    render: (record) => <RenderText text={record?.qpayMerchantOwnerFirstName || "-"} />,
  },
  {
    title: "Өргөрөг",
    sorter: true,
    dataIndex: "qpayMerchantLat",
    render: (record) => <RenderText text={record?.qpayMerchantLat || "-"} />,
  },
  {
    title: "Уртраг",
    sorter: true,
    dataIndex: "qpayMerchantLng",
    render: (record) => <RenderText text={record?.qpayMerchantLng || "-"} />,
  },
  {
    title: "Аймаг, нийслэл",
    sorter: true,
    dataIndex: "qpayMerchantProvince",
    render: (record) => <RenderText text={(zipCodes || []).find((c) => c.code === record.qpayMerchantProvince)?.name || "-"} />,
  },
  {
    title: "Сум, дүүрэг",
    sorter: true,
    dataIndex: "qpayMerchantDistrict",
    render: (record) => <RenderText text={(zipCodes || []).find((c) => c.code === record.qpayMerchantDistrict)?.name || "-"} />,
  },
  {
    title: "Хаяг",
    sorter: true,
    dataIndex: "qpayMerchantAddress",
    render: (record) => <RenderText text={record?.qpayMerchantAddress || "-"} />,
  },
  {
    title: "Утас",
    sorter: true,
    dataIndex: "qpayMerchantPhone",
    render: (record) => <RenderText text={record?.qpayMerchantPhone || "-"} />,
  },
  {
    title: "Имэйл",
    sorter: true,
    dataIndex: "qpayMerchantEmail",
    render: (record) => <RenderText text={record?.qpayMerchantEmail || "-"} />,
  },
  {
    title: "Бүртгэсэн ажилтан",
    sorter: true,
    dataIndex: "businessName",
    render: (record) => <RenderText text={renderName(record?.qpayMerchantRegUser)} />,
  },
  {
    title: "Бүртгэсэн огноо",
    sorter: true,
    dataIndex: "createdAt",
    render: (record) => <RenderText text={dateFormat(record?.createdAt)} />,
  },
];
